import { ref } from "vue";
import axios, { AxiosResponse } from 'axios';
import { ApiActions, ApiGetters, FormActions, FormGetters } from './../Store/index';
import { isEmpty } from './functions';
import {callPaq, getInput, updateStoreValueAndFilter, validation} from './../Composables/form';
import dayjs from 'dayjs';

const locale = 'de-DE';

let lastRequestParams = '';
let lastResult = '';
let lastServiceCheckTimestamp: number | null = null;

interface LocalStorageItem {
    value: any;
    expiry: number;
}

export async function checkIfServiceIsRunning(serverHost: string) {
    let isVisibleForm = ref(true);
    let serviceDownIntervalShortEval = ref(0);

    const setVisibleForm = (value: boolean) => {
        isVisibleForm.value = value;
    }

    const checkIfServiceIsRunningShortEvalForm = async () => {
        const headers = new Headers();
        headers.append("Origin", `https://${serverHost}/`);

        let userAgent = navigator.userAgent.toLowerCase();
        let isBot = /bot|crawl|slurp|spider/i.test(userAgent);

        try {
            const response = await fetch(
                `https://${serverHost}/FinanceLine/health/?action=isAppAlive`,
                {
                    method: "GET",
                    mode: "cors",
                    headers: headers,
                }
            );

            if (isBot || (response && response.status === 200)) {
                const data = await response.text();
                if (isBot || (!isNaN(parseInt(data)) && parseInt(data) >= 0)) {
                    if (!isVisibleForm.value) {
                        setVisibleForm(true);
                    }
                } else {
                    if (isVisibleForm.value) {
                        setVisibleForm(false);
                    }
                }
            } else {
                if (isVisibleForm.value) {
                    setVisibleForm(false);
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    const checkServiceDownIntervalShortEvalForm = () => {
        checkIfServiceIsRunningShortEvalForm().then(() => {
            if (isVisibleForm.value) {
                clearInterval(serviceDownIntervalShortEval.value);
            }
        });
    }


    // Check if the function was called within the last 30 seconds (30000 ms)
    if (lastServiceCheckTimestamp && Date.now() - lastServiceCheckTimestamp < 30000) {
        setVisibleForm(true);
    } else {
        serviceDownIntervalShortEval.value = setInterval(
            checkServiceDownIntervalShortEvalForm,
            5000
        )

        // Update the timestamp after calling the service
        lastServiceCheckTimestamp = Date.now();
    }

    return isVisibleForm;
}

export async function fetchSelectionInteractive(store: any) {
    const allowedVehicleTypes = store.getters[`form/${FormGetters.GET_DATA}`].allowedVehicleTypes.value,
        identificationByKba = store.getters[`form/${FormGetters.GET_DATA}`].identificationByKba.value,
        filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value,
        useHsnTsn = store.getters[`form/${FormGetters.GET_INPUTS}`].useHsnTsn.value,
        datECodes = store.getters[`form/${FormGetters.GET_DATA}`].hsnTsnService.datECodes,
        serverHost = store.getters[`api/${ApiGetters.GET_SERVER_HOST}`];

    if (!isFormReadyForApiCall(store)) {
        return;
    }

    let response: any = {},
        timer = 2000,
        params = new URLSearchParams();

    const isServiceRunning = await checkIfServiceIsRunning(serverHost);

    store.dispatch(`form/${FormActions.SET_IS_VISIBLE_FORM}`, isServiceRunning);
    if (!isServiceRunning) {
        // If service is not running, wait for 5 seconds and then call fetchSelectionInteractive again
        setTimeout(() => fetchSelectionInteractive(store), 5000);
        return;
    }

    store.dispatch(`api/${ApiActions.INCREMENT_ON_GOING_REQUESTS}`);
    await toggleLoadingAnimation(true);

    params.set('function', 'executeVehicleSelectionInteractive');

    if (allowedVehicleTypes && !identificationByKba) {
        for (let i = 0; i < allowedVehicleTypes.length; i++) {
            params.set('parameters[allowedVehicleTypes][' + i + ']', allowedVehicleTypes[i]);
        }
    }

    if (filter && Object.keys(filter).length) {
        Object.entries(filter).forEach((entry, index) => {
            const [key, value] = entry;

            if (key === 'hsn' || key === 'tsn' || key === 'mileage' || key === 'location' || key === 'email') {
                // Skip this iteration
                return;
            }

            if (key === 'firstRegistration' && value) {
                if (typeof value === 'string' || typeof value === 'number') {
                    const dateValue = dayjs(value, 'MM/YYYY').valueOf()
                    const firstRegistration = new Date(dateValue);
                    const formattedDate = `${firstRegistration.getFullYear()}-${
                        String(firstRegistration.getMonth() + 1).padStart(2, '0')
                    }-${String(firstRegistration.getDate()).padStart(2, '0')}`;

                    params.set('parameters[registrationDate]', formattedDate);
                }
            } else {
                params.set('parameters[filter][' + index + ']', JSON.stringify({
                    "key": key,
                    "value": value
                }));
            }
        });
    }

    if (datECodes) {
        for (let i = 0; i < datECodes.length; i++) {
            params.set('parameters[datECodes][' + i + ']', datECodes[i]);
        }
    }

    // @ts-ignore
    if (params.size === 1) {
        timer = 0;
    }

    await createJsonRequest('selection', params, store);

    if (lastResult && lastResult !== '{}') {
        response = JSON.parse(lastResult);
    }

    if (response.data) {
        store.dispatch(`api/${ApiActions.SET_IS_INTENSIVE_USED_API}`, false);
        if (response.data.filters) {
            for (let i = 0; i < response.data.filters.length; i++) {
                if (response.data.filters[i].key === 'manufacturer') {
                    store.dispatch(`api/${ApiActions.UPDATE_DATA}`,{key: 'vehicles', value: response.data.filters[i].proposals});
                }
            }

            store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'filters', key: 'value', value: response.data.filters});
        }

        if (response.data.count) {
            store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'carCount', key: 'value', value: response.data.count});
            if (useHsnTsn) {
                store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'carCountHsnTsn', key: 'value', value: response.data.count});
            }
            if (response.data.vehicles && response.data.count === 1) {
                store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'selectedCar', key: 'value', value: response.data.vehicles[0]});
            } else {
                const vehicleSelected = store.getters[`form/${FormGetters.GET_INPUTS}`]['vehicleSelected'];
                if (vehicleSelected && !vehicleSelected.valid && !isEmpty(vehicleSelected.value)) {
                    store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'selectedCar', key: 'value', value: {}});
                }
            }
        } else {
            store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'carCount', key: 'value', value: 0});

            if (useHsnTsn) {
                store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'carCountHsnTsn', key: 'value', value: 0});
            }
        }

        if (response.data.vehicles) {
            let vehicles = Object.values(response.data.vehicles).sort(function(car1: any, car2: any) {
                if (car1.constructionTimeFrom > car2.constructionTimeFrom) {
                    return 1;
                } else if (car1.constructionTimeFrom < car2.constructionTimeFrom) {
                    return -1;
                } else {
                    if (car1.constructionTimeTo > car2.constructionTimeTo) {
                        return 1;
                    } else if(car1.constructionTimeTo < car2.constructionTimeTo) {
                        return -1;
                    }
                }
                return 0;
            });

            let allEquipmentCount = {};
            Object.keys(vehicles).forEach(key => {
                for(let key2 in vehicles[key].equipments) {
                    let refKey = key2 + ':' + vehicles[key].equipments[key2];
                    if(!allEquipmentCount[refKey]) {
                        allEquipmentCount[refKey] = {
                            'count' : 0,
                            'key' : key2,
                            'value' : vehicles[key].equipments[key2]
                        };
                    }
                    allEquipmentCount[refKey].count++;
                }
                for(let key2 in vehicles[key].equipmentsSuper) {
                    let refKey = key2 + ':' + vehicles[key].equipmentsSuper[key2];
                    if(!allEquipmentCount[refKey]) {
                        allEquipmentCount[refKey] = {
                            'count' : 0,
                            'key' : key2,
                            'value' : vehicles[key].equipmentsSuper[key2]
                        };
                    }
                    allEquipmentCount[refKey].count++;
                }
            });

            Object.keys(vehicles).forEach(key => {
                for (let refKey in allEquipmentCount) {
                    if (allEquipmentCount[refKey].count === vehicles.length) {
                        continue;
                    }
                    if (!vehicles[key].differentEquipments) {
                        vehicles[key].differentEquipments = {};
                    }
                    if (vehicles[key].equipments[allEquipmentCount[refKey].key]) {
                        vehicles[key].differentEquipments[allEquipmentCount[refKey].key] = vehicles[key].equipments[allEquipmentCount[refKey].key];
                    } else if (vehicles[key].equipmentsSuper[allEquipmentCount[refKey].key]) {
                        vehicles[key].differentEquipments[allEquipmentCount[refKey].key] = vehicles[key].equipmentsSuper[allEquipmentCount[refKey].key];
                    }
                }

            });

            store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'vehicleSelection', key: 'value', value: vehicles});
        }
    }
    else {
        const response = store.getters[`api/${ApiGetters.GET_RESPONSE}`];
        if (response && Object.keys(response).length > 0 && response.isBadRequest && response.code === 19) {
            store.dispatch(`api/${ApiActions.SET_IS_INTENSIVE_USED_API}`, true);
        }
    }

    await validation(store, timer);

    store.dispatch(`api/${ApiActions.DECREMENT_ON_GOING_REQUESTS}`);
    await toggleLoadingAnimation(false);
}

export async function getVehicleIdentificationByKba(store: any) {
    const hsnTsnService = store.getters[`form/${FormGetters.GET_DATA}`].hsnTsnService;
    store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'hsnTsnService', key: 'datECodes', value: '' });
    store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'filter', key: 'value', value: {}});

    let kba = hsnTsnService.fieldHSN + '/' + hsnTsnService.fieldTSN;

    if (kba.match(/^[0-9]{4}\/[A-Za-z0-9]{3}$/)) {
        let params      = new URLSearchParams();
        params.set('function', 'getVehicleIdentificationByKba');
        params.set('parameters[kba]', kba);

        await createJsonRequest('selection', params, store);
        const response = JSON.parse(lastResult);

        if ('data' in response && response.data && Object.keys(response.data).length !== 0) {
            if (response.data.Dossier) {
                // _paq
                callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', 'datECodes');

                let datEcodeArray: string[] = [];
                response.data.Dossier.forEach((item: any) => {
                    datEcodeArray.push(item.Vehicle.DatECode as string);
                }, datEcodeArray);

                store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'hsnTsnService', key: 'datECodes', value: datEcodeArray });

                await fetchSelectionInteractive(store);
                const newResponse = JSON.parse(lastResult);

                if (newResponse.data) {
                    if (newResponse.data.filters && newResponse.data.vehicles) {
                        for (let i = 0; i < newResponse.data.filters.length; i++) {
                            if(newResponse.data.filters[i].proposals){
                                const item = newResponse.data.filters[i];
                                if(item.proposals.length === 1) {
                                    const inputResult = getInput(store, item.key);

                                    if (inputResult !== null) {
                                        const { key, input } = inputResult;

                                        if (key) {
                                            await updateStoreValueAndFilter(
                                                store,
                                                {
                                                    name: key,
                                                    filterProperty: ['selection', item.key]
                                                },
                                                newResponse.data.filters[i].proposals[0].k,
                                                true,
                                                false,
                                                true,
                                                false,
                                                false
                                            );
                                        }
                                    }
                                }
                            }
                        }

                        if (newResponse.data.count > 1) {
                            store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'hsnTsnService', key: 'datECodes', value: '' });
                        }

                        // call AJAX for new data from the API
                        await fetchSelectionInteractive(store);
                    }

                    if (newResponse.data.count) {
                        store.dispatch(
                            `form/${FormActions.UPDATE_DATA}`,
                            { field: 'carCountHsnTsn', key: 'value', value: newResponse.data.count }
                        );
                    }
                }
            }
        } else {
            store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'carCountHsnTsn', key: 'value', value: 0 });
        }
    }
}

export async function valuateService(store: any) {
    const firstRegistrationValue = store.getters[`form/${FormGetters.GET_INPUTS}`].firstRegistration.value,
        mileage = store.getters[`form/${FormGetters.GET_INPUTS}`].mileage.value,
        location = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldLocation.value,
        selectedCar = store.getters[`form/${FormGetters.GET_DATA}`].selectedCar.value,
        filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;

    if (isEmpty(firstRegistrationValue) || isEmpty(mileage) || isEmpty(selectedCar)) {
        return;
    }

    const dateValue = dayjs(firstRegistrationValue, 'MM/YYYY').valueOf()
    const firstRegistration = new Date(dateValue);
    const formattedDate= `${firstRegistration.getFullYear()}-${
        String(firstRegistration.getMonth() + 1).padStart(2, '0')
    }-${String(firstRegistration.getDate()).padStart(2, '0')}`;

    let params = new URLSearchParams();

    params.set('function', 'getVehicleEvaluation');
    params.set('parameters[mileage]', mileage);
    params.set('parameters[datECode]', ("" + selectedCar.ecode).substring(0,15));
    params.set('parameters[registrationDate]', formattedDate);
    params.set('parameters[container]', ("" + selectedCar.ecode).slice(-5));

    if (!isEmpty(location)) {
        params.set('parameters[zip]', location);
    }
    if (!isEmpty(filter) && filter['fuelMethod']) {
        params.set('parameters[fuelMethod]', filter['fuelMethod']);
    }

    await createJsonRequest('valuate', params, store);

    if (lastResult && Object.keys(lastResult).length) {
        const response = JSON.parse(lastResult);

        if (response && response.data) {
            let result = response.data;

            if (!isEmpty(result['DatECode'])) {
                result['DatECodeFormatted'] = result['DatECode'].substr(0, 2) + ' ' + result['DatECode'].substr(2, 3) +
                    ' ' + result['DatECode'].substr(5, 3) + ' ' + result['DatECode'].substr(8, 3) +
                    ' ' + result['DatECode'].substr(11, 4);
            }

            if (result['PurchasePriceGross']) {
                result['PurchasePriceGross'] = Math.round(Number(result['PurchasePriceGross'])/100)*100;

                // _paq
                callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'Bewertung', 'Wert', result.PurchasePriceGross);
            }

            store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'valuateServiceResult', key: 'value', value: result});
        }
    }
}

export async function sendCdpApi(store: any, selectedCarData: string = '') {
    const result = store.getters[`form/${FormGetters.GET_DATA}`].valuateServiceResult.value;

    if (
        !isEmpty(result) &&
        result.PurchasePriceGross &&
        store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties.mileage
    ) {
        let params = new URLSearchParams(store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties);

        params.set('action', 'setShortEvaluationData');
        params.set('estimatedCarValue', result.PurchasePriceGross);

        if (selectedCarData) {
            params.set('selectedCarData', selectedCarData);
        }
        if (result['DatECode']) {
            params.set('image', result['DatECode'].substring(0, 11));
        }

        try {
            const response: AxiosResponse = await axios.post('/cdpApi.json', params);

            if (response.status === 200) {
                if (response.data) {
                    const uid = response.data.uid;
                    setLocalStorageWithExpiry('sEvalUid', uid, 4320000);
                    setLocalStorageWithExpiry('vehicleFormEmail', store.getters[`form/${FormGetters.GET_INPUTS}`].fieldEmail.value, 4320000);
                    localStorage.setItem('l_type_of_request', '1');
                }
            }
        } catch (error) {
            // Handle errors here
            console.error('Error in valuateService:', error);
        }
    }
}

export async function toggleLoadingAnimation(status: boolean) {
    const overlay = document.querySelector("#loading-overlay");
    const iconSvg = document.querySelector("#loading-icon-svg");
    const ready = document.querySelector("#loading-ready");
    const body = document.querySelector("body");

    overlay!.classList.toggle("hidden", !status);
    iconSvg!.classList.toggle("hidden", !status);
    ready!.classList.toggle("ready", !status);

    if (body && body.classList.contains('loading-selenium')) {
        body.classList.remove('loading-selenium');
    }
}

export function isLoadingAnimationEnabled(): boolean {
    const overlay = document.querySelector("#loading-overlay");
    const iconSvg = document.querySelector("#loading-icon-svg");

    // Check if the elements exist and do not have the 'hidden' class
    const overlayVisible = overlay ? !overlay.classList.contains("hidden") : false;
    const iconSvgVisible = iconSvg ? !iconSvg.classList.contains("hidden") : false;

    // Return true if both overlay and iconSvg are visible (i.e., not hidden)
    return overlayVisible && iconSvgVisible;
}

export async function getImagesForVehicles(store: any) {
    const selectedCar: {} = store.getters[`form/${FormGetters.GET_DATA}`].selectedCar.value;
    const cacheImages: {} = store.getters[`form/${FormGetters.GET_DATA}`].cache.images;
    let ecodesFirstSegment: {} = {};

    if (Object.keys(selectedCar).length) {
        let vehicle = JSON.parse(JSON.stringify(selectedCar))

        selectedCar['mainTypeNameWithoutYear'] = vehicle['mainTypeName'].replace(/\s*\(\s*([0-9]{2}\.)?[0-9]{4}\s*->\s*\)/, '');
        selectedCar['buildYearRange'] = vehicle['containerName'].replace(/.*(\d{4} - \d{4}).*/, '$1');

        const ecode = vehicle.ecode.substring(0, 11);

        if (cacheImages[ecode]) {
            selectedCar['imageBase64Encoded'] = cacheImages[ecode];
            selectedCar['imageFetched'] = true;

            store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'selectedCar', key: 'value', value: selectedCar});
            return;
        }

        ecodesFirstSegment[ecode] = true;
        selectedCar['imageBase64Encoded'] = '';

        Object.keys(ecodesFirstSegment).forEach(ecode => {
            loadBase64ImageFromEcode(store, ecode).then(image => {
                let vehicle = JSON.parse(JSON.stringify(selectedCar));
                if (
                    ecode === vehicle.ecode.substring(0, 11) &&
                    typeof image === "string"
                ) {
                    selectedCar['imageBase64Encoded'] = image;
                    selectedCar['imageFetched'] = true;

                    store.dispatch(`form/${ApiActions.UPDATE_DATA}`, {field: 'cache', key: 'images', value: {...cacheImages, [ecode]: image}});
                    store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'selectedCar', key: 'value', value: selectedCar});
                }
            });
        });
    }
}

export function getMatomoNameKey(nameInput: string) {
    switch (nameInput) {
        case 'fuelMethod':
            return 'fuelType';
        case 'mainTypeGroupFb':
            return 'carModel';
        case 'countOfDoors':
            return 'numberOfDoors';
        case 'structureType':
            return 'carModelType';
        case 'powerKw':
            return 'power';
        case 'engine':
            return 'engine';
        case 'driveType':
            return 'driveType';
        case 'gearType':
            return 'transmissionType';
        case 'gearbox':
            return 'transmissionModel';
        case 'mainTypeFb':
            return 'carModel_precise';
        case 'subType':
            return 'carModelVersion';
        case 'constructionYearFrom':
            return 'constructionYearFrom';
        case 'constructionYearTo':
            return 'constructionYearTo';
        case 'manufacturer':
            return 'manufacturer';
        case 'location':
            return 'zipCode';
        case 'mileage':
            return 'mileage';
        default:
            return '';
    }
}

export function getItemByKey(
    store: any,
    key: string,
    keyName = 'k',
    items: any[] | null = null,
    field = 'manufacturer'
): any | null {
    // Default to using the filters value if items is not provided
    if (!items) {
        // Get the filters from the store
        const filters = store.getters[`form/${FormGetters.GET_DATA}`].filters.value;

        // Ensure filters is an array and handle it safely
        if (filters && Array.isArray(filters)) {
            // Find the field item based on the provided field name
            const fieldItem = filters.find((filter: any) => filter.key === field);

            // If a matching field item is found and has proposals, update items
            if (fieldItem && Array.isArray(fieldItem.proposals)) {
                items = fieldItem.proposals;
            } else {
                // If no matching field or proposals, return null
                return null;
            }
        } else {
            // If filters are invalid, return null
            return null;
        }
    }

    // Check if items is an array and has elements before proceeding
    if (!Array.isArray(items) || !items.length) {
        return null;
    }

    // Find the item where keyName matches the provided key
    const foundItem = items.find((item: any) => item[keyName]?.trim() === key?.trim());

    // Return the found item or null if no match is found
    return foundItem ?? null;
}

export function isFormReadyForApiCall(store) {
  const vehicleValue = store.getters[`form/${FormGetters.GET_INPUTS}`].vehicle.value,
    mileageValue = store.getters[`form/${FormGetters.GET_INPUTS}`].mileage.value,
    firstRegistrationValue = store.getters[`form/${FormGetters.GET_INPUTS}`].firstRegistration.value,
    useHsnTsn = store.getters[`form/${FormGetters.GET_INPUTS}`].useHsnTsn.value,
    fieldHSN = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldHSN.value,
    fieldTSN = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldTSN.value,
    isResettingForm = store.getters[`form/${FormGetters.GET_IS_RESETTING_FORM}`],
    isFormTouched = store.getters[`form/${FormGetters.GET_IS_TOUCHED}`];

  if (useHsnTsn) {
    if (isFormTouched && !isResettingForm && (isEmpty(fieldHSN) || isEmpty(fieldTSN) || isEmpty(mileageValue) || isEmpty(firstRegistrationValue))) {
      return false;
    }
  } else {
    if (isFormTouched && !isResettingForm && (isEmpty(vehicleValue) || isEmpty(mileageValue) || isEmpty(firstRegistrationValue))) {
      return false;
    }
  }

    store.dispatch(`form/${FormActions.SET_IS_RESSETING_FORM}`, false);

    return true;
}

async function loadBase64ImageFromEcode(store: any, ecode: string) {
    let params: URLSearchParams = new URLSearchParams();

    params.set('function', 'getVehicleImages');
    params.set('parameters[aspect]', 'ANGULARFRONT');
    params.set('parameters[imageType]', 'PICTURE');
    params.set('parameters[datECode]', ecode);

    await createJsonRequest('selection', params, store);

    if (lastResult && Object.keys(lastResult).length) {
        const response: AxiosResponse = JSON.parse(lastResult);

        if (response && response.data) {
            return response.data;
        }
    }
}

async function createJsonRequest(type: string, params: URLSearchParams, store: any): Promise<void> {
    const apiUrl = store.getters[`api/${ApiGetters.GET_URL}`];
    params.set("locale", locale);
    const paramsAsRequestString = params.toString();

    // Check if the new request parameters are the same as the last request
    if (lastRequestParams === paramsAsRequestString) {
        return;
    }

    store.dispatch(`api/${ApiActions.INCREMENT_ON_GOING_REQUESTS}`);
    await toggleLoadingAnimation(true);

    lastRequestParams = paramsAsRequestString;

    try {
        const response: AxiosResponse = await axios.post(apiUrl, params);

        setResponseData(response, store);

        if (response.status === 200 && !response.data.error) {
            lastResult = JSON.stringify(response);
        } else {
            lastResult = JSON.stringify({});
        }
    } catch (error) {
        // Handle errors here
        console.error('Error in createJsonRequest:', error);
        setTimeout(async () => {
            // Fetch new data
            await toggleLoadingAnimation(false);
        }, 1000);
    } finally {
        // Decrement the counter when a request completes
        store.dispatch(`api/${ApiActions.DECREMENT_ON_GOING_REQUESTS}`);

        // Disable loading animation if all requests are done
        if (store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`] === 0) {
            setTimeout(async () => {
                // Fetch new data
                await toggleLoadingAnimation(false);
            }, 1000);
        }
    }
}

function setResponseData (response: any, store: any) {
    const responseData = response.data;

    const isBadRequest = !!response.data.error;
    const httpCode = responseData.error ? responseData.error.httpcode : 0;
    const errorObject = responseData.error ? responseData.error : {};

    const code = errorObject.code ? errorObject.code : '';
    const message = errorObject.string ? errorObject.string : '';

    store.dispatch(`api/${ApiActions.SET_RESPONSE}`, {
        'isBadRequest': isBadRequest,
        'httpCode': httpCode,
        'code': code,
        'message': message
    });
}

function setLocalStorageWithExpiry(key: string, value: any, ttl: number): void {
    const now = new Date();

    // `item` is an object which contains the original value
    // as well as the time when it's supposed to expire
    const item: LocalStorageItem = {
        value: value,
        expiry: now.getTime() + ttl,
    };

    localStorage.removeItem(key);
    localStorage.setItem(key, JSON.stringify(item));
}
